import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useSubscription, SUBSCRIPTION_TRUST_TTL_MS } from '@/hooks/use-subscription'
import { e2ePaymentIssue, isE2EEntitlementBypassEnabled, isE2EFreePlan } from '@/lib/e2e-auth'
import { isTransientQueryError } from '@/lib/query-retry'
import { isSupabaseConfigured, supabase } from '@/lib/supabase-client'
import type { SubscriptionData, PlanType, PaymentIssue } from '@/services/billing-api'

interface SubscriptionContextValue {
  /** Authenticated account owning the subscription query */
  sessionUserId: string | null
  /** Verified e-mail belonging to the same authenticated session */
  sessionEmail: string | null
  /** Current subscription data (null while loading) */
  subscription: SubscriptionData | null
  /** Shortcut: current plan type */
  plan: PlanType
  /** Whether user has Pro plan */
  isPro: boolean
  /** Whether account is paused by admin */
  isPaused: boolean
  /** Days remaining on trial (null if not on trial) */
  trialDaysRemaining: number | null
  /** Days remaining in grace period (null if not in grace period) */
  gracePeriodDaysRemaining: number | null
  /** Whether user is in active grace period (past_due + days remaining) */
  isGracePeriod: boolean
  /** Whether grace period has expired (access revoked) */
  isGraceExpired: boolean
  /** Whether gift subscription has expired */
  isGiftExpired: boolean
  /** Whether trial has expired */
  isTrialExpired: boolean
  /** Whether cancel is pending at period end */
  isCancelPending: boolean
  /** Whether subscription data is loading */
  isLoading: boolean
  /** Whether entitlement could not be verified: no successful response yet, or the last one is older than the trust TTL */
  entitlementUnknown: boolean
  /** Whether the unknown state is being retried automatically (transient error). False on permanent 4xx. */
  entitlementRetrying: boolean
  /** Ödeme sorunu (kart/yan menü uyarısı); erişim anahtarı kapalı ya da eski backend için null */
  paymentIssue: PaymentIssue | null
  /** Check if a feature is available */
  hasFeature: (feature: string) => boolean
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  // Oturum durumunu reaktif olarak izle. Bu provider auth guard'ının dışında
  // (tüm App'i sarar) olduğundan, token yokken billing query'sini tetiklememek için
  // session varlığını burada kontrol edip `enabled` guard'ı olarak geçiriyoruz.
  const [sessionIdentity, setSessionIdentity] = useState<{
    userId: string | null
    email: string | null
  }>({ userId: null, email: null })
  const authRevisionRef = useRef(0)
  const { userId: sessionUserId, email: sessionEmail } = sessionIdentity

  // Üretim DIŞI E2E hook'u. `RequireAuth` bu bayrakla oturumu bypass ettiği için gerçek
  // bir Supabase oturumu yoktur; oturum yoksa abonelik sorgusu da `enabled:false` olur ve
  // `proOnly` sayfalar Upgrade duvarına düşer. Yani auth bypass'ı tek başına yetmiyordu.
  // Bypass'ın entitlement karşılığı burada verilir: dev/test build'inde Pro sayılır.
  // Bu kapi oturum bypass'indan DAHA DAR: yalniz Vite dev sunucusunda acilir, hicbir
  // `vite build` ciktisinda acilmaz (bkz. lib/e2e-auth.ts).
  const [e2eEntitlementBypass] = useState(() => isE2EEntitlementBypassEnabled())

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setSessionIdentity({ userId: null, email: null })
      return
    }

    let mounted = true
    const restoreRevision = authRevisionRef.current

    // İlk yüklemede storage'dan session restore
    void supabase.auth.getSession().then(({ data }) => {
      if (mounted && authRevisionRef.current === restoreRevision) {
        setSessionIdentity({
          userId: data.session?.user?.id ?? null,
          email: data.session?.user?.email ?? null,
        })
      }
    })

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        authRevisionRef.current += 1
        setSessionIdentity({
          userId: session?.user?.id ?? null,
          email: session?.user?.email ?? null,
        })
      }
    })

    return () => {
      mounted = false
      listener.subscription.unsubscribe()
    }
  }, [])

  const { data, error, isError, isLoading, dataUpdatedAt, errorUpdatedAt } = useSubscription({
    enabled: !!sessionUserId,
    userId: sessionUserId,
  })

  // TTL duvar saatine bağlıdır. Kalıcı hatada periyodik refetch yoktur; yalnız hata zamanına
  // bakılsaydı süre hiç ilerlemez ve güven süresiz uzardı. Bu yüzden TTL'in dolacağı ana
  // tek bir zamanlayıcı kurup yeniden değerlendiriyoruz.
  const [trustCheckedAt, setTrustCheckedAt] = useState(() => Date.now())

  useEffect(() => {
    if (!isError || dataUpdatedAt <= 0) return
    const remaining = dataUpdatedAt + SUBSCRIPTION_TRUST_TTL_MS - Date.now()
    if (remaining <= 0) {
      setTrustCheckedAt(Date.now())
      return
    }
    const timer = setTimeout(() => setTrustCheckedAt(Date.now()), remaining)
    return () => clearTimeout(timer)
  }, [isError, dataUpdatedAt, errorUpdatedAt])

  const value = useMemo<SubscriptionContextValue>(() => {
    if (e2eEntitlementBypass) {
      const e2eFree = isE2EFreePlan() // #1029: yalnız dev önizleme; varsayılan Pro
      return {
        sessionUserId,
        sessionEmail,
        subscription: null,
        plan: e2eFree ? 'free' : 'pro',
        isPro: !e2eFree,
        isPaused: false,
        trialDaysRemaining: null,
        gracePeriodDaysRemaining: null,
        isGracePeriod: false,
        isGraceExpired: false,
        isGiftExpired: false,
        isTrialExpired: false,
        isCancelPending: false,
        isLoading: false,
        entitlementUnknown: false,
        entitlementRetrying: false,
        paymentIssue: e2ePaymentIssue(),
        hasFeature: () => !e2eFree,
      }
    }

    const plan = data?.plan_type ?? 'free'
    const isPaused = !!data?.paused
    // Geçici hata (5xx/ağ) kendiliğinden düzelmesi beklenen hatadır; yalnız onda son bilinen
    // plana TTL süresince güvenilir. Kalıcı 4xx'te yeniden deneme yoktur, dolayısıyla güven
    // uzatılmaz: entitlement anında "bilinmiyor" olur.
    const errorIsTransient = isTransientQueryError(error)
    const trustExpired =
      dataUpdatedAt > 0 &&
      (!errorIsTransient || trustCheckedAt - dataUpdatedAt > SUBSCRIPTION_TRUST_TTL_MS)
    const entitlementUnknown = !!sessionUserId && isError && (!data || trustExpired)
    const entitlementRetrying = entitlementUnknown && errorIsTransient

    // Calculate trial days remaining
    let trialDaysRemaining: number | null = null
    if (data?.trial_ends_at) {
      const diff = new Date(data.trial_ends_at).getTime() - Date.now()
      trialDaysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
    }

    // Calculate grace period days remaining
    let gracePeriodDaysRemaining: number | null = null
    if (data?.grace_period_until) {
      const diff = new Date(data.grace_period_until).getTime() - Date.now()
      gracePeriodDaysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
    }

    // Computed billing states
    const isGracePeriod = data?.status === 'past_due' && gracePeriodDaysRemaining !== null && gracePeriodDaysRemaining > 0
    const isGraceExpired = !!data?.grace_period_expired
    const isGiftExpired = !!data?.gift_expired
    const isTrialExpired = !!data?.trial_expired
    const isCancelPending = !!data?.cancel_at_period_end || !!data?.cancel_at

    return {
      sessionUserId,
      sessionEmail,
      subscription: data ?? null,
      plan,
      isPro: !entitlementUnknown && plan === 'pro' && !isPaused,
      isPaused,
      trialDaysRemaining,
      gracePeriodDaysRemaining,
      isGracePeriod,
      isGraceExpired,
      isGiftExpired,
      isTrialExpired,
      isCancelPending,
      isLoading,
      entitlementUnknown,
      entitlementRetrying,
      paymentIssue: entitlementUnknown ? null : (data?.payment_issue ?? null),
      hasFeature: (feature: string) => {
        if (entitlementUnknown) return false
        if (isPaused) return false
        if (!data) return false
        return data.features?.[feature] ?? (plan === 'pro')
      },
    }
  }, [data, dataUpdatedAt, e2eEntitlementBypass, error, isError, isLoading, sessionEmail, sessionUserId, trustCheckedAt])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscriptionContext(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) {
    // Return safe defaults when used outside provider (e.g. public pages)
    return {
      sessionUserId: null,
      sessionEmail: null,
      subscription: null,
      plan: 'free',
      isPro: false,
      isPaused: false,
      trialDaysRemaining: null,
      gracePeriodDaysRemaining: null,
      isGracePeriod: false,
      isGraceExpired: false,
      isGiftExpired: false,
      isTrialExpired: false,
      isCancelPending: false,
      isLoading: false,
      entitlementUnknown: false,
      entitlementRetrying: false,
      paymentIssue: null,
      hasFeature: () => false,
    }
  }
  return ctx
}
